// Base Libs
import { useState, InputEvent, useEffect, useRef } from "react";
import { shallowEqual } from "react-redux";
import {
    GuxFormFieldNumber,
    GuxFormFieldTextarea,
    GuxTooltip,
} from "genesys-spark-components-react";

// Styles
import "./AnswerOptionInput.less";

// General common
import { DefaultLimits } from "constants/DefaultLimits";
import { useAppDispatch, useAppSelector } from "app/hooks";
import FormType from "enums/FormType";
import FeatureToggle from "enums/FeatureToggle";

// Feature commons
import { qualityFormActions } from "features/qualityForm/qualityFormSlice";
import {
    selectFormType,
    selectQuestion,
    selectIsAnswerDefaultAnswer,
} from "features/qualityForm/qualityFormSlices/qualityFormCommons";
import { useStrings } from "features/i18n/useStrings";
import { selectAnswerOption } from "features/qualityForm/qualityFormSlices/index";
import { DeleteButton, ReorderButtons } from "features/common/ActionIcons";
import { Card } from "features/common/Card";
import {
    invalidIntegerInputCharacters,
    selectIsAnswerOptionTextPresent,
    selectIsAnswerOptionTextUnderMaxLength,
    selectIsAnswerOptionValid,
} from "features/qualityForm/qualityFormValidations";
import {
    isAnswerOptionPointsEmpty,
    isAnswerOptionPointsInDecimalNumber,
    isAnswerOptionPointsOverLimits,
} from "features/qualityForm/qualityFormValidations/qualityFormAnswerValidations";

// Feature slices
import {
    DependentIds,
    EntityType,
    initiateAnswerOptionDeletion,
} from "features/qualityForm/qualityFormSlice";
import { selectFeatureToggle } from "features/featureToggles/featureToggleSlice";

// Form editor commons
import { ErrorAction } from "../common/ErrorBox/VisibilityConflictErrorBox";
import { VisibilityConflictErrorBoxContainer } from "../common/ErrorBox/VisibilityConflictErrorBoxContainer";
import { ErrorStatement } from "../common/ErrorStatement/ErrorStatement";
import { disableInputScroll } from "../common/DisableInputScroll";

// Locals
import { EvaluationAssistance } from "./EvaluationAssistance";
import ScoringOptionsSection from "./ScoringOptionsSection";
import { DefaultAnswerCheckbox } from "./DefaultAnswerCheckbox";

// Locales
import strings from "./Question.strings";
import { announceAfterStateUpdate } from "features/qualityForm/utils/screenReaderAnnouncement";

function MultipleSelectAnswerOptionInput({
    questionId,
    multipleSelectOptionId,
    canDeleteAnswer,
    answerIndex,
}: {
    questionId: string;
    multipleSelectOptionId: string;
    canDeleteAnswer: boolean;
    answerIndex: number;
}) {
    const t = useStrings(strings);

    const answerOption = useAppSelector((state) =>
        selectAnswerOption(state, multipleSelectOptionId, true)
    );
    const dispatch = useAppDispatch();
    const [visibilityDependentIds, setVisibilityDependentIds] =
        useState<DependentIds>();
    const isOptionNamePresent = useAppSelector((state) =>
        selectIsAnswerOptionTextPresent(state, multipleSelectOptionId, true)
    );
    const formType = useAppSelector(selectFormType);
    const isOptionNameTooLong = useAppSelector(
        (state) =>
            !selectIsAnswerOptionTextUnderMaxLength(
                state,
                multipleSelectOptionId,
                true
            )
    );
    const answerPointsErrorMessage = useAppSelector((state) => {
        if (isAnswerOptionPointsEmpty(state, multipleSelectOptionId, true)) {
            return t.errors.emptyPoints;
        } else if (
            isAnswerOptionPointsInDecimalNumber(
                state,
                multipleSelectOptionId,
                true
            )
        ) {
            return t.errors.decimalPoints;
        } else if (
            isAnswerOptionPointsOverLimits(state, multipleSelectOptionId, true)
        ) {
            if (formType === FormType.EVALUATION) {
                return t.errors.decimalPoints;
            } else {
                return t.errors.answerPointsOverUpperLimit({
                    maxAnswerOptionPoints: DefaultLimits.maxAnswerOptionPoints,
                });
            }
        } else {
            return undefined;
        }
    });

    const numberInputRef = useRef<HTMLInputElement>(null);
    useEffect(() => disableInputScroll(numberInputRef));

    const isDefaultAnswerForMultiSelect = useAppSelector((state) =>
        selectIsAnswerDefaultAnswer(
            state,
            questionId,
            multipleSelectOptionId,
            true
        )
    );

    return (
        <>
            <VisibilityConflictErrorBoxContainer
                visibilityDependentIds={visibilityDependentIds}
                clearVisibilityConflicts={() =>
                    setVisibilityDependentIds(undefined)
                }
                entityType={EntityType.Answer}
                errorAction={ErrorAction.Delete}
            >
                <div className="AnswerOptionInput">
                    <div className="answer-field">
                        <div className="answer-index">{answerIndex + 1}.</div>
                        <GuxFormFieldTextarea
                            label-position="screenreader"
                            className="text-input"
                            resize="auto"
                        >
                            <textarea
                                slot="input"
                                name="questionName"
                                required
                                value={answerOption?.text}
                                onInput={({ currentTarget }) => {
                                    dispatch(
                                        qualityFormActions.changeAnswerText({
                                            text: currentTarget.value,
                                            answerOptionId:
                                                multipleSelectOptionId,
                                            isMultipleSelect: true,
                                        })
                                    );
                                }}
                            ></textarea>
                            <label slot="label">
                                {t.labels.answerOptionTextInput}
                            </label>
                        </GuxFormFieldTextarea>
                    </div>
                    <GuxFormFieldNumber
                        className="points-input"
                        labelPosition="beside"
                    >
                        <input
                            slot="input"
                            type="number"
                            ref={numberInputRef}
                            required={true}
                            min="0"
                            step="1"
                            max={
                                formType === FormType.EVALUATION
                                    ? undefined
                                    : DefaultLimits.maxAnswerOptionPoints
                            }
                            value={answerOption?.value}
                            onBeforeInput={(
                                event: InputEvent<HTMLInputElement>
                            ) => {
                                if (
                                    invalidIntegerInputCharacters.includes(
                                        event.data
                                    )
                                ) {
                                    event.preventDefault();
                                }
                            }}
                            onChange={({ currentTarget }) => {
                                dispatch(
                                    qualityFormActions.changeAnswerValue({
                                        value: currentTarget.value,
                                        answerOptionId: multipleSelectOptionId,
                                        isMultipleSelect: true,
                                    })
                                );
                            }}
                        ></input>
                        <label slot="label"> {t.labels.points} </label>
                    </GuxFormFieldNumber>
                    <div data-testid="delete-button-box">
                        <DeleteButton
                            onDelete={
                                canDeleteAnswer
                                    ? () => {
                                          const visibilityConflicts = dispatch(
                                              initiateAnswerOptionDeletion(
                                                  questionId,
                                                  multipleSelectOptionId,
                                                  true
                                              )
                                          ) as unknown as DependentIds;
                                          if (
                                              visibilityDependentIds
                                                  ?.questionGroupIds.length !==
                                                  0 ||
                                              visibilityDependentIds
                                                  ?.questionIds.length !== 0
                                          ) {
                                              setVisibilityDependentIds(
                                                  visibilityConflicts
                                              );
                                          }
                                      }
                                    : undefined
                            }
                        />
                        {!canDeleteAnswer && (
                            <GuxTooltip>
                                {t.labels.cannotDeleteMSQOption({
                                    minAnswerOptionCount:
                                        DefaultLimits.minMultipleSelectQuestionOptionCount,
                                })}
                            </GuxTooltip>
                        )}
                    </div>
                </div>
            </VisibilityConflictErrorBoxContainer>
            {(!isOptionNamePresent ||
                answerPointsErrorMessage ||
                isOptionNameTooLong) && (
                <div className="answer-option-error">
                    {!isOptionNamePresent && (
                        <ErrorStatement errorMessage={t.errors.requiredField} />
                    )}
                    {answerPointsErrorMessage && (
                        <ErrorStatement
                            errorMessage={answerPointsErrorMessage}
                        />
                    )}
                    {isOptionNameTooLong && (
                        <ErrorStatement
                            errorMessage={t.errors.answerOptionTextTooLong({
                                maxAnswerTextLength:
                                    DefaultLimits.maxAnswerTextLength,
                            })}
                        />
                    )}
                </div>
            )}
            <DefaultAnswerCheckbox
                questionId={questionId}
                optionId={multipleSelectOptionId}
                isMultipleSelect={true}
                isDefaultAnswer={isDefaultAnswerForMultiSelect}
                answerIndex={answerIndex}
            />
            <EvaluationAssistance
                questionId={questionId}
                answerId={multipleSelectOptionId}
                isMultipleSelect={true}
            />
        </>
    );
}

export function MultipleSelectAnswerOption({
    questionId,
    multipleSelectOptionId,
    answerIndex,
    canDeleteAnswer,
}: {
    questionId: string;
    multipleSelectOptionId: string;
    answerIndex: number;
    canDeleteAnswer: boolean;
}) {
    const t = useStrings(strings);
    const dispatch = useAppDispatch();
    // Importing all FTs
    const { isMultipleSelectFTEnabled } = useAppSelector((state) => ({
        isMultipleSelectFTEnabled: selectFeatureToggle(
            state,
            FeatureToggle.MULTIPLE_SELECT
        ),
    }));
    const isAnswerValid = useAppSelector((state) =>
        selectIsAnswerOptionValid(state, multipleSelectOptionId, true)
    );
    const { canMoveUp, canMoveDown } = useAppSelector((state) => {
        const question = selectQuestion(state, questionId);
        const answerCount = (question?.multipleSelectOptionQuestions ?? [])
            .length;
        return {
            canMoveUp: answerIndex > 0,
            canMoveDown: answerIndex < answerCount - 1,
        };
    }, shallowEqual);
    const answerOption = useAppSelector((state) =>
        selectAnswerOption(state, multipleSelectOptionId, true)
    );

    return (
        <div className="AnswerOption" data-testid="answer-option">
            <ReorderButtons
                onMoveUp={
                    isMultipleSelectFTEnabled && canMoveUp && answerOption
                        ? () => {
                              dispatch(
                                  qualityFormActions.moveAnswer({
                                      answerOptionId: multipleSelectOptionId,
                                      newRelativePosition: -1,
                                      questionId: questionId,
                                      indexOfSelectedAnswerWithinQuestion:
                                          answerIndex,
                                  })
                              );

                              announceAfterStateUpdate(
                                  dispatch,
                                  qualityFormActions.setScreenReaderAnnouncement,
                                  qualityFormActions.clearScreenReaderAnnouncement,
                                  t.screenreader.answerMoveUp({
                                      currentPosition: answerIndex + 1,
                                      newPosition: answerIndex,
                                      answerOptionName: answerOption.text ?? "",
                                  })
                              );
                          }
                        : undefined
                }
                onMoveDown={
                    isMultipleSelectFTEnabled && canMoveDown && answerOption
                        ? () => {
                              dispatch(
                                  qualityFormActions.moveAnswer({
                                      answerOptionId: multipleSelectOptionId,
                                      newRelativePosition: +1,
                                      questionId: questionId,
                                      indexOfSelectedAnswerWithinQuestion:
                                          answerIndex,
                                  })
                              );

                              announceAfterStateUpdate(
                                  dispatch,
                                  qualityFormActions.setScreenReaderAnnouncement,
                                  qualityFormActions.clearScreenReaderAnnouncement,
                                  t.screenreader.answerMoveDown({
                                      currentPosition: answerIndex + 1,
                                      newPosition: answerIndex + 2,
                                      answerOptionName: answerOption.text ?? "",
                                  })
                              );
                          }
                        : undefined
                }
            />
            <Card className={`${isAnswerValid ? "" : "error"}`}>
                <MultipleSelectAnswerOptionInput
                    questionId={questionId}
                    multipleSelectOptionId={multipleSelectOptionId}
                    canDeleteAnswer={canDeleteAnswer}
                    answerIndex={answerIndex}
                />
                <ScoringOptionsSection
                    questionId={multipleSelectOptionId}
                    isMultipleSelect={true}
                />
            </Card>
        </div>
    );
}
